/**
 * Initial values for an offering form, from what the person said in the chat.
 *
 * The assistant's `request_form` event can carry `prefill`: "a new laptop,
 * mine is five years old and very slow" becomes the device type and the reason.
 * The server has already checked every item against the offering; this is the
 * browser's own, second pass over the same rules, because the form must never
 * show a value it cannot submit:
 *
 *   - an id the form does not have is ignored;
 *   - a pick-list keeps only option ids it actually lists (a `SELECT` exactly one);
 *   - text is cut to the parameter's `max_length`;
 *   - attachments, directory lookups and unknown types are never prefilled, and
 *     an answer already seeded (the requester's own name) is never overwritten —
 *     with one exception: when the person said the request is for a colleague,
 *     the server repoints the "who is this for" field's `default_value` at that
 *     colleague (found in the directory) and sends a matching item. Only an
 *     item naming exactly that default is accepted for a directory lookup.
 *
 * Everything stays editable; filing still goes through prepare + submit.
 *
 * Pure and import-free (types only) so `node --test` can load it from source.
 * The shapes below are structural twins of `OfferingParameter` / `AnswerState`
 * narrowed to what this reads.
 */

/** The parts of an `OfferingParameter` a prefill is checked against. */
export interface PrefillParameter {
  parameter_id: string;
  type?: string | null;
  max_length?: number | null;
  lookup?: string | null;
  default_value?: string | null;
  options?: { value_id: string }[] | null;
}

/** One validated initial value, as the stream carries it. */
export interface PrefillItem {
  parameter_id: string;
  value?: string | null;
  value_ids?: string[] | null;
}

/** One parameter's answer, in the form's own shape. */
export interface PrefillAnswer {
  value: string | null;
  valueIds: string[];
  attachmentIds?: string[];
}

export interface AppliedPrefill {
  /** The seed answers with every usable prefill written over empty fields. */
  answers: Record<string, PrefillAnswer>;
  /** The parameters that received a value, for the "from your message" hint. */
  prefilledIds: string[];
}

const SCALAR_TYPES = new Set(["TEXT", "TEXTAREA", "NUMBER", "DATE", "DATETIME", "BOOLEAN"]);

/**
 * Write the prefill onto the form's initial answers.
 *
 * @param parameters The offering's parameters, as rendered.
 * @param seed The answers the form would otherwise start with.
 * @param prefill The event's `prefill`; absent on older servers.
 */
export function applyPrefill(
  parameters: readonly PrefillParameter[],
  seed: Record<string, PrefillAnswer>,
  prefill: readonly PrefillItem[] | null | undefined,
): AppliedPrefill {
  const answers: Record<string, PrefillAnswer> = { ...seed };
  const prefilledIds: string[] = [];
  const byId = new Map(parameters.map((parameter) => [parameter.parameter_id, parameter]));

  for (const item of prefill ?? []) {
    const parameter = byId.get(item.parameter_id);
    if (parameter === undefined || prefilledIds.includes(item.parameter_id)) {
      continue;
    }
    const vettedPerson = onBehalfAnswer(parameter, item);
    if (vettedPerson !== null) {
      // The seed already holds the same id (it is the default); this only
      // marks it as coming from the conversation.
      answers[item.parameter_id] = vettedPerson;
      prefilledIds.push(item.parameter_id);
      continue;
    }
    if (hasAnswer(answers[item.parameter_id])) {
      continue;
    }
    const answer = prefillAnswer(parameter, item);
    if (answer === null) {
      continue;
    }
    answers[item.parameter_id] = answer;
    prefilledIds.push(item.parameter_id);
  }

  return { answers, prefilledIds };
}

/**
 * A directory lookup's answer from the server's on-behalf item, or null.
 *
 * Accepted only when the item names the parameter's own `default_value` —
 * the colleague the server looked up and wrote there — never any other id.
 */
function onBehalfAnswer(parameter: PrefillParameter, item: PrefillItem): PrefillAnswer | null {
  if (parameter.lookup == null || parameter.lookup === "") {
    return null;
  }
  const expected = parameter.default_value ?? "";
  const type = (parameter.type ?? "UNKNOWN").toUpperCase();
  const usesIds = type === "SELECT" || type === "MULTISELECT";
  const given = usesIds ? (item.value_ids ?? [])[0] : item.value;
  if (expected === "" || given !== expected) {
    return null;
  }
  return usesIds ? { value: null, valueIds: [expected] } : { value: expected, valueIds: [] };
}

/** The answer one prefill item becomes, or null when it does not fit. */
function prefillAnswer(parameter: PrefillParameter, item: PrefillItem): PrefillAnswer | null {
  if (parameter.lookup != null && parameter.lookup !== "") {
    return null;
  }
  const type = (parameter.type ?? "UNKNOWN").toUpperCase();

  if (type === "SELECT" || type === "MULTISELECT") {
    const known = new Set((parameter.options ?? []).map((option) => option.value_id));
    const chosen = [...new Set((item.value_ids ?? []).filter((id) => known.has(id)))];
    if (chosen.length === 0 || (type === "SELECT" && chosen.length !== 1)) {
      return null;
    }
    return { value: null, valueIds: chosen };
  }

  if (!SCALAR_TYPES.has(type)) {
    return null;
  }
  let value = (item.value ?? "").trim();
  if (parameter.max_length != null && parameter.max_length > 0) {
    value = value.slice(0, parameter.max_length);
  }
  if (type === "BOOLEAN" && value !== "true" && value !== "false") {
    return null;
  }
  return value === "" ? null : { value, valueIds: [] };
}

function hasAnswer(answer: PrefillAnswer | undefined): boolean {
  if (answer === undefined) {
    return false;
  }
  return (
    answer.valueIds.length > 0 ||
    (answer.value ?? "") !== "" ||
    (answer.attachmentIds ?? []).length > 0
  );
}

/**
 * The parameter marked required when the page holds it so (28 Oct): the
 * access wizard's 365FO "Company / entity", optional on the Ivanti form but
 * needed to name its approvers. Already required, or not listed: as it is.
 * The asterisk, the yellow "still needed" tint and the held Next all read
 * `required`, so this is the only change a form needs.
 */
export function withRequiredIds<P extends { parameter_id: string; required?: boolean | null }>(
  parameter: P,
  requiredIds: ReadonlySet<string>,
): P {
  return parameter.required === true || !requiredIds.has(parameter.parameter_id)
    ? parameter
    : { ...parameter, required: true };
}
